import { type QueueJobName } from "@prisma/client";
import PgBoss from "pg-boss";
import { log } from "winston";
import { config } from "~/config";
import { type AppContext } from "~/lib/context";
import { makeDatabaseUrl } from "~/lib/context/makeContext";
import { logError, logInfo } from "~/lib/logger";
import { type BaseJobData } from "~/lib/queue/baseJobDataSchema";
import { getWorkers, type WorkerParams } from "~/lib/queue/getWorkers";
import { makeHandler } from "~/lib/queue/makeHandler";
import { type QueueDriver, type SendOptions } from "~/lib/queue/makeQueueDriver";

export const EXIT_SUCCESS = 0;

export class PgBossDriver implements QueueDriver {
  private boss: PgBoss;

  private started = false;

  constructor() {
    this.boss = new PgBoss(makeDatabaseUrl(config.database.url, { forWorker: true }));
    this.boss.on("error", (error) => log("error", `[worker] Queue error`, { error }));
  }

  async start(ctx: AppContext) {
    if (this.started) return;

    // kind of duplicate with above but we have the context here
    this.boss.on("error", (error) => logError(ctx, "[worker] Queue error", { error }));

    await this.boss.start();
    this.started = true;

    logInfo(ctx, "[worker] pg-boss queue driver started");
  }

  async initWorkers(ctx: AppContext) {
    this.handleProcessStop(ctx);

    await this.start(ctx);

    const promises = getWorkers().map((worker) => this.addWorker(ctx, worker));

    await Promise.all(promises);
  }

  async sendJob<T extends BaseJobData>(ctx: AppContext, jobName: QueueJobName, data: T, options: SendOptions) {
    await this.start(ctx);

    return this.boss.send(jobName, data, options);
  }

  private async addWorker<T extends BaseJobData>(ctx: AppContext, params: WorkerParams<T>) {
    const { jobName } = params;
    logInfo(ctx, "[worker] Adding worker for job", { jobName });

    const handler = makeHandler(ctx, params);

    await this.boss.work(jobName, { includeMetadata: true }, handler);

    logInfo(ctx, "[worker] Worker for job has been added", { jobName });
  }

  private handleProcessStop(ctx: AppContext) {
    // The promise returned by `boss.stop()` is resolved immediately.
    // We need to wait for all active jobs to have been completed before gracefully exiting the process.
    this.boss.on("stopped", () => {
      process.exit(EXIT_SUCCESS);
    });

    process.on("SIGINT", async () => {
      logInfo(ctx, "[worker] SIGINT received, exiting process");

      await this.boss.stop({ graceful: true });
    });

    process.on("SIGTERM", async () => {
      logInfo(ctx, "[worker] SIGTERM received, exiting process");

      await this.boss.stop({ graceful: true });
    });
  }
}
